import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import { formatBookingDate } from "../Calendar";
import { USERS } from "../../config/trainers";
import { buildApiUrl } from "../../config/api";

function readLocalBookings() {
  try {
    const value = JSON.parse(localStorage.getItem("kokorin_bookings") || "[]");
    return Array.isArray(value) ? value : [];
  } catch { return []; }
}

export default function ActiveBookings({ refreshSignal, onCancelled }) {
  const [bookings, setBookings] = useState(readLocalBookings);
  const [open, setOpen] = useState(false);
  const [serverStatuses, setServerStatuses] = useState({});
  const [confirmToken, setConfirmToken] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setBookings(readLocalBookings());
  }, [refreshSignal]);

  const fetchStatuses = useCallback(async (list) => {
    const results = {};
    await Promise.allSettled(
      list.map(async (b) => {
        try {
          const res = await fetch(buildApiUrl(`/api/requests/by-token/${b.cancelToken}`));
          if (res.ok) {
            results[b.cancelToken] = await res.json();
          }
        } catch {}
      })
    );
    setServerStatuses(results);
  }, []);

  useEffect(() => {
    if (open && bookings.length) {
      fetchStatuses(bookings);
    }
  }, [open, bookings, fetchStatuses]);

  const handleCancel = async (token) => {
    if (cancelling) return;
    setCancelling(true);
    setError("");
    try {
      const res = await fetch(buildApiUrl(`/api/requests/cancel/${token}`), {
        method: "POST",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Помилка");
      }
      const updated = readLocalBookings().filter((b) => b.cancelToken !== token);
      try { localStorage.setItem("kokorin_bookings", JSON.stringify(updated)); } catch {}
      setBookings(updated);
      setConfirmToken(null);
      onCancelled?.();
      setServerStatuses((prev) => {
        const next = { ...prev };
        delete next[token];
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const trainerLabel = (id) =>
    USERS.find((u) => u.id === id)?.label || id;

  if (!bookings.length && !open) return null;

  const confirmBooking = bookings.find((b) => b.cancelToken === confirmToken);

  return (
    <>
      <button
        className="active-bookings-card"
        onClick={() => setOpen(true)}
      >
        Мої записи · {bookings.length}
      </button>

      <Dialog
        maxWidth={false}
        aria-labelledby="my-bookings-title"
        slotProps={{ paper: { className: "booking-dialog" }, backdrop: { className: "booking-backdrop" } }}
        open={open}
        onClose={() => {
          if (cancelling) return;
          setOpen(false);
          setConfirmToken(null);
          setError("");
        }}
      >
        <div className="booking-dialog-header"><span className="dialog-brand">dance<span>sfera</span></span><button className="dialog-close" aria-label="Закрити вікно" disabled={cancelling} onClick={() => { setOpen(false); setConfirmToken(null); setError(""); }}>×</button></div>
        <div className="booking-dialog-body">
          {error && <p className="booking-error" role="alert">{error}</p>}
            {confirmToken ? (
              <>
                <h2 id="my-bookings-title">
                  Підтвердження скасування
                </h2>
                <p className="dialog-description">
                  Скасувати запис?
                  <br />
                  <strong>
                    {trainerLabel(confirmBooking?.trainer)} •{" "}
                    {formatBookingDate(confirmBooking?.date)} • {confirmBooking?.time}
                  </strong>
                </p>
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => handleCancel(confirmToken)}
                    disabled={cancelling}
                    className="studio-primary"
                    style={{ flex: 1 }}
                  >
                    {cancelling ? "..." : "Скасувати"}
                  </button>
                  <button
                    onClick={() => setConfirmToken(null)}
                    className="studio-secondary"
                    style={{ flex: 1 }}
                  >
                    Назад
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 id="my-bookings-title">
                  Мої активні записи
                </h2>
                <div className="active-bookings-list">
                  {!bookings.length && <p className="dialog-description">У тебе поки немає активних записів.</p>}
                  {bookings.map((b) => {
                    const srv = serverStatuses[b.cancelToken];
                    const isCancelled = srv?.status === "failed";
                    return (
                      <div
                        key={b.cancelToken}
                        className={`active-booking-item${isCancelled ? " cancelled" : ""}`}
                      >
                        <div className="active-booking-info">
                          <strong>{trainerLabel(b.trainer)}</strong>
                          <span>
                            {formatBookingDate(b.date)} · {b.time}
                          </span>
                          <span>
                            {b.name} {b.surname}
                          </span>
                          {isCancelled && (
                            <span className="cancelled-badge">Скасовано</span>
                          )}
                        </div>
                        {!isCancelled && (
                          <button
                            onClick={() => setConfirmToken(b.cancelToken)}
                            className="cancel-btn-small"
                          >
                            Скасувати
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="studio-secondary"
                  style={{ marginTop: 4 }}
                >
                  Закрити
                </button>
              </>
            )}
        </div>
      </Dialog>
    </>
  );
}
